class IndexedDBService {
  constructor(dbName, version = 1) {
    this.dbName = dbName;
    this.version = version;
    this.db = null;
  }

  // Initialize the database
  async init(storeName, keyPath) {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = (event) => {
        reject('Error opening database');
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName, { keyPath: keyPath });
        }
      };
    });
  }

  // Create/Add a new record
  async add(storeName, data) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.add(data);

      request.onsuccess = () => resolve(data);
      request.onerror = () => reject('Error adding data');
    });
  }

  // Get a record by key
  async get(storeName, key) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject('Error getting data');
    });
  }

  // Get all records
  async getAll(storeName) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject('Error getting all data');
    });
  }

  // Update a record
  async update(storeName, data) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put(data);

      request.onsuccess = () => resolve(data);
      request.onerror = () => reject('Error updating data');
    });
  }

  // Delete a record
  async delete(storeName, key) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(key);

      request.onsuccess = () => resolve(key);
      request.onerror = () => reject('Error deleting data');
    });
  }

  // Clear all records in a store
  async clear(storeName) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject('Error clearing store');
    });
  }

  // Close the database connection
  close() {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
}

// Create a singleton instance manager
class IndexedDBManager {
  constructor() {
    this.dbInstances = new Map();
  }

  async getDBInstance(dbName, storeName, keyPath, version = 1) {
    if (!this.dbInstances.has(dbName)) {
      const db = new IndexedDBService(dbName, version);
      await db.init(storeName, keyPath);
      this.dbInstances.set(dbName, db);
    }
    return this.dbInstances.get(dbName);
  }

  closeDB(dbName) {
    const db = this.dbInstances.get(dbName);
    if (db) {
      db.close();
      this.dbInstances.delete(dbName);
    }
  }

  closeAllDBs() {
    for (const [dbName, db] of this.dbInstances) {
      db.close();
    }
    this.dbInstances.clear();
  }
}

// Create a singleton instance
const indexedDBManager = new IndexedDBManager();

export default indexedDBManager; 